// Users / auth / AI employees API — extracted from api.js (Tier 2 #9a round 3).
// Login + logout, builder-user management, regular-user CRUD, org-user
// assignments (apps + agents), user roles/groups, AI employees and their
// roles/groups/assignments.

import api, { invalidateOrgsCache, invalidateMeCache } from './api';
import { setTokens, getRefreshToken, clearTokens, clearReturnTo } from './auth';

// ─── Auth ──────────────────────────────────────────────────────────────────

// Public: request a one-time reset code by email. Always resolves on 200 —
// backend deliberately doesn't leak whether the email exists.
export const requestResetCode = async (email) => {
  const response = await api.post("/auth/forgot-password", { email });
  return response.data;
};

// Public: verify the emailed 6-digit code. On success the backend returns a
// short-lived signed reset ticket (`reset_token`) that authorizes the
// password step; the OTP is NOT consumed here (only on reset-password).
// The code is only ever the user's own input — no server secret is persisted.
export const verifyResetCode = async (email, code) => {
  const response = await api.post("/auth/verify-reset-code", { email, code });
  return response.data;
};

// Public: set the new password using the reset ticket from verifyResetCode.
// The ticket is held in memory only (never URL/localStorage), so email/code
// are not resent here — the token IS the proof.
export const resetPassword = async (resetToken, newPassword) => {
  const response = await api.post("/auth/reset-password", {
    reset_token: resetToken,
    new_password: newPassword,
  });
  return response.data;
};

// Authed: change your own password. Requires the current password.
export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.post("/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return response.data;
};

export const login = async (email, password) => {
  // Drop any /auth/me cached under a previous session (SPA login, no reload).
  invalidateMeCache();
  const response = await api.post("/auth/login", { email, password });
  if (response.data.access_token) {
    setTokens(response.data.access_token, response.data.refresh_token);
  }
  if (response.data.user?.id) {
    localStorage.setItem("builder_user_id", String(response.data.user.id));
  }
  return response.data;
};

// ─── SAML SSO ────────────────────────────────────────────────────────────────

// Public: whether SSO is enabled (drives the login-page button). Never throws
// to the caller path — the login page treats any failure as "disabled".
export const fetchSamlConfig = async () => {
  const response = await api.get("/saml/config");
  return response.data; // { enabled: bool, provider: string }
};

// Full-page URL the SSO button navigates to. A hard nav (not XHR) is required so
// the IdP redirect round-trip works. api.defaults.baseURL already ends in /api.
export const samlLoginUrl = () => `${api.defaults.baseURL}/saml/login`;

// Exchange the one-time ?code= (from the SAML ACS callback redirect) for real
// tokens, storing them exactly like password login.
export const exchangeSsoCode = async (code) => {
  const response = await api.post("/saml/exchange", { code });
  if (response.data.access_token) {
    setTokens(response.data.access_token, response.data.refresh_token);
  }
  if (response.data.user?.id) {
    localStorage.setItem("builder_user_id", String(response.data.user.id));
  }
  return response.data;
};

export const logout = async () => {
  try {
    const refreshToken = getRefreshToken();
    await api.post("/auth/logout", refreshToken ? { refresh_token: refreshToken } : {});
  } catch {}
  // Manual logout MUST drop any stashed return-path. Otherwise a user who hit
  // an unexpected logout earlier (which set returnTo), then logged out manually,
  // would be bounced back to that old path on next login.
  clearReturnTo();
  clearTokens();
  localStorage.removeItem("builder_user_id");
  // Defensive: the hard reload below already wipes module-level state, but
  // invalidating here documents the intent and protects future variants of
  // this flow that might switch to router.navigate (no reload, cache leaks).
  invalidateOrgsCache();
  invalidateMeCache();
  window.location.href = "/login";
};

export const getBuilderUserId = () => localStorage.getItem("builder_user_id");

// Re-export auth helpers for backward compat
export { hasPermission, getTokenPayload } from './auth';

// ─── Builder Roles ─────────────────────────────────────────────────────────
export const fetchBuilderRoles = async () => {
  const response = await api.get("/builder-roles");
  return response.data;
};

// ─── Builder Users ─────────────────────────────────────────────────────────
export const fetchBuilderUsers = async (params) => {
  const response = await api.get("/builder-users", params ? { params } : undefined);
  return response.data;
};

export const fetchBuilderUser = async (id) => {
  const response = await api.get(`/builder-users/${id}`);
  return response.data;
};

export const createBuilderUser = async (data) => {
  const response = await api.post("/builder-users", data);
  return response.data;
};

export const updateBuilderUser = async (id, data) => {
  const response = await api.put(`/builder-users/${id}`, data);
  return response.data;
};

export const deleteBuilderUser = async (id) => {
  const response = await api.delete(`/builder-users/${id}`);
  return response.data;
};

// ─── Users ─────────────────────────────────────────────────────────────────
export const fetchUsers = async (params) => {
  const response = await api.get("/users", params ? { params } : undefined);
  return response.data;
};

export const createUser = async (data) => {
  const response = await api.post("/users", data);
  return response.data;
};

export const updateUser = async (userId, data) => {
  const response = await api.put(`/users/${userId}`, data);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await api.delete(`/users/${userId}`);
  return response.data;
};

// Alias for fetchUsers
export const fetchAllUsers = fetchUsers;

// ─── Org Users & Assignments ───────────────────────────────────────────────

// Minimal {id,name,email} picker for the "Import Users" modal — platform
// users not yet in this org. No search term returns the latest registered
// users; a search term ILIKE-matches across the full platform user base.
// Deliberately no page/offset params: the picker is search-driven, not paginated.
export const fetchImportableUsers = async (orgId, { search, limit } = {}) => {
  const params = {};
  if (search) params.search = search;
  if (limit) params.limit = limit;
  const response = await api.get(`/orgs/${orgId}/users/importable`, { params });
  return response.data;
};

export const importUserToOrg = async (orgId, userId) => {
  const response = await api.post(`/orgs/${orgId}/users/import`, { user_id: userId });
  return response.data;
};

export const removeUserFromOrg = async (orgId, userId) => {
  const response = await api.delete(`/orgs/${orgId}/users/${userId}`);
  return response.data;
};

export const fetchUserAssignments = async (orgId, userId) => {
  const response = await api.get(`/orgs/${orgId}/users/${userId}/assignments`);
  return response.data;
};

export const assignApp = async (orgId, userId, appId, isAdmin = false) => {
  const response = await api.post(`/orgs/${orgId}/users/${userId}/apps/${appId}`, { is_admin: isAdmin });
  return response.data;
};

export const unassignApp = async (orgId, userId, appId) => {
  const response = await api.delete(`/orgs/${orgId}/users/${userId}/apps/${appId}`);
  return response.data;
};

export const updateAppAssignment = async (orgId, userId, appId, isAdmin) => {
  const response = await api.patch(`/orgs/${orgId}/users/${userId}/apps/${appId}`, { is_admin: isAdmin });
  return response.data;
};

export const assignAgent = async (orgId, userId, agentId) => {
  const response = await api.post(`/orgs/${orgId}/users/${userId}/agents/${agentId}`);
  return response.data;
};

export const unassignAgent = async (orgId, userId, agentId) => {
  const response = await api.delete(`/orgs/${orgId}/users/${userId}/agents/${agentId}`);
  return response.data;
};

// ─── User Roles & Groups ───────────────────────────────────────────────────
export const fetchUserRoles = async (orgId, userId) => {
  const response = await api.get(`/orgs/${orgId}/users/${userId}/roles`);
  return response.data;
};

export const setUserRoles = async (orgId, userId, roleIds) => {
  const response = await api.put(`/orgs/${orgId}/users/${userId}/roles`, { role_ids: roleIds });
  return response.data;
};

export const fetchUserGroups = async (orgId, userId) => {
  const response = await api.get(`/orgs/${orgId}/users/${userId}/groups`);
  return response.data;
};

export const setUserGroups = async (orgId, userId, groupIds) => {
  const response = await api.put(`/orgs/${orgId}/users/${userId}/groups`, { group_ids: groupIds });
  return response.data;
};

// ─── AI Employees ──────────────────────────────────────────────────────────
export const fetchAIEmployees = async (orgId) => {
  const response = await api.get(`/orgs/${orgId}/ai-employees`);
  return response.data;
};

export const createAIEmployee = async (orgId, data) => {
  const response = await api.post(`/orgs/${orgId}/ai-employees`, data);
  return response.data;
};

export const updateAIEmployee = async (orgId, id, data) => {
  const response = await api.put(`/orgs/${orgId}/ai-employees/${id}`, data);
  return response.data;
};

export const deleteAIEmployee = async (orgId, id) => {
  const response = await api.delete(`/orgs/${orgId}/ai-employees/${id}`);
  return response.data;
};

export const fetchAIEmployeeRoles = async (orgId, id) => {
  const response = await api.get(`/orgs/${orgId}/ai-employees/${id}/roles`);
  return response.data;
};

export const setAIEmployeeRoles = async (orgId, id, roleIds) => {
  const response = await api.put(`/orgs/${orgId}/ai-employees/${id}/roles`, { role_ids: roleIds });
  return response.data;
};

export const fetchAIEmployeeGroups = async (orgId, id) => {
  const response = await api.get(`/orgs/${orgId}/ai-employees/${id}/groups`);
  return response.data;
};

export const setAIEmployeeGroups = async (orgId, id, groupIds) => {
  const response = await api.put(`/orgs/${orgId}/ai-employees/${id}/groups`, { group_ids: groupIds });
  return response.data;
};

export const fetchAIEmployeeAssignments = async (orgId, id) => {
  const response = await api.get(`/orgs/${orgId}/ai-employees/${id}/assignments`);
  return response.data;
};

export const assignAIEmployeeApp = async (orgId, id, appId) => {
  const response = await api.post(`/orgs/${orgId}/ai-employees/${id}/apps/${appId}`);
  return response.data;
};

export const unassignAIEmployeeApp = async (orgId, id, appId) => {
  const response = await api.delete(`/orgs/${orgId}/ai-employees/${id}/apps/${appId}`);
  return response.data;
};

export const assignAIEmployeeAgent = async (orgId, id, agentId) => {
  const response = await api.post(`/orgs/${orgId}/ai-employees/${id}/agents/${agentId}`);
  return response.data;
};

export const unassignAIEmployeeAgent = async (orgId, id, agentId) => {
  const response = await api.delete(`/orgs/${orgId}/ai-employees/${id}/agents/${agentId}`);
  return response.data;
};
